const CART_UPDATE_EVENT = 'wetheme-cart-update';
const TOGGLE_RIGHT_DRAWER_EVENT = 'wetheme-toggle-right-drawer';

// The cart drawer is rendered with v-if, so the component is recreated when the
// drawer is closed. Keeping this outside the component preserves the disclosure
// state for the lifetime of the page without persisting it across refreshes.
const cartCrossSellsPageState = {
  expanded: false
};

let sectionId = 0;

class CartCrossSells extends HTMLElement {
  constructor() {
    super();

    this.cartSignature = null;
    this.requestToken = 0;
    this.requestController = null;
    this.cartController = null;
    this.swiper = null;
    this.sliderInitTimer = null;

    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleDrawerToggle = this.handleDrawerToggle.bind(this);
    this.handleToggleClick = this.handleToggleClick.bind(this);
  }

  connectedCallback() {
    window.wetheme?.webcomponentRegistry?.register({ key: 'component-cart-cross-sells' });
    this.cartSignature = null;
    this.renderShell();
    document.documentElement.addEventListener(CART_UPDATE_EVENT, this.handleCartUpdate);
    document.documentElement.addEventListener(TOGGLE_RIGHT_DRAWER_EVENT, this.handleDrawerToggle);
    this.fetchCart();
  }

  disconnectedCallback() {
    document.documentElement.removeEventListener(CART_UPDATE_EVENT, this.handleCartUpdate);
    document.documentElement.removeEventListener(TOGGLE_RIGHT_DRAWER_EVENT, this.handleDrawerToggle);
    this.destroySlider();
    this.cartController?.abort();
    this.requestController?.abort();
    this.requestToken += 1;
    this.cartSignature = null;
  }

  renderShell() {
    const currentSectionId = `cart-cross-sells-${++sectionId}`;
    const panelId = `${currentSectionId}-panel`;

    const section = document.createElement('section');
    section.className = 'f-cart-cross-sells';
    section.setAttribute('aria-labelledby', `${currentSectionId}-toggle`);

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.id = `${currentSectionId}-toggle`;
    toggle.className = 'f-cart-cross-sells__toggle';
    toggle.setAttribute('aria-controls', panelId);
    toggle.addEventListener('click', this.handleToggleClick);

    const label = document.createElement('span');
    label.className = 'f-cart-cross-sells__label';
    label.textContent = this.dataset.title || '';

    const icon = document.createElement('span');
    icon.className = 'f-cart-cross-sells__chevron';
    icon.setAttribute('aria-hidden', 'true');

    toggle.append(label, icon);

    const panel = document.createElement('div');
    panel.id = panelId;
    panel.className = 'f-cart-cross-sells__panel';
    panel.setAttribute('role', 'region');
    panel.setAttribute('aria-labelledby', toggle.id);

    const slider = document.createElement('div');
    slider.className = 'f-cart-cross-sells__rail swiper-container';

    const rail = document.createElement('div');
    rail.className = 'f-cart-cross-sells__wrapper swiper-wrapper';
    slider.append(rail);

    const status = document.createElement('p');
    status.className = 'f-cart-cross-sells__status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.hidden = true;

    panel.append(slider, status);
    section.append(toggle, panel);
    this.replaceChildren(section);

    this.section = section;
    this.toggle = toggle;
    this.panel = panel;
    this.slider = slider;
    this.rail = rail;
    this.status = status;

    this.setExpanded(cartCrossSellsPageState.expanded);
    this.setVisible(false);
  }

  handleToggleClick() {
    this.setExpanded(!cartCrossSellsPageState.expanded);
  }

  setExpanded(expanded) {
    cartCrossSellsPageState.expanded = Boolean(expanded);

    if (!this.toggle || !this.panel) {
      return;
    }

    this.toggle.setAttribute('aria-expanded', String(cartCrossSellsPageState.expanded));
    this.toggle.classList.toggle('is-expanded', cartCrossSellsPageState.expanded);
    this.panel.hidden = !cartCrossSellsPageState.expanded;

    if (cartCrossSellsPageState.expanded && this.rail?.querySelector('.swiper-slide')) {
      if (this.swiper?.update) {
        this.swiper.update();
      } else {
        this.scheduleSliderInit();
      }
    }
  }

  setVisible(visible) {
    this.hidden = !visible;

    const slot = this.parentElement;
    if (slot?.classList.contains('f-cart-cross-sells-slot')) {
      slot.hidden = !visible;
    }
  }

  setStatus(message = '', busy = false) {
    if (!this.status) {
      return;
    }

    this.status.textContent = message;
    this.status.hidden = !message;
    this.status.setAttribute('aria-busy', String(busy));
  }

  handleCartUpdate(event) {
    const cart = event.detail?.cart;
    if (cart) {
      this.syncCart(cart);
    }
  }

  handleDrawerToggle(event) {
    const detail = event.detail || {};
    if (detail.type && detail.type !== 'cart') {
      return;
    }

    if (detail.params?.cart) {
      this.syncCart(detail.params.cart);
    }
  }

  async fetchCart() {
    this.cartController?.abort();
    this.cartController = new AbortController();

    const token = this.requestToken;

    try {
      const response = await fetch(this.dataset.cartUrl, {
        credentials: 'same-origin',
        signal: this.cartController.signal,
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Unable to load cart: ${response.status}`);
      }

      const cart = await response.json();
      if (this.isConnected && token === this.requestToken) {
        this.syncCart(cart);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.setVisible(false);
      }
    }
  }

  syncCart(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    if (!items.length || Number(cart?.item_count) < 1) {
      this.requestController?.abort();
      this.requestToken += 1;
      this.cartSignature = null;
      this.destroySlider();
      this.rail?.replaceChildren();
      this.setVisible(false);
      return;
    }

    const signature = [...new Set(
      items.map(item => String(item.product_id || ''))
    )].sort().join('|');

    if (signature === this.cartSignature) {
      return;
    }

    this.cartSignature = signature;
    this.requestController?.abort();
    this.requestController = new AbortController();
    const token = ++this.requestToken;

    this.renderLoading();

    this.loadRecommendations(items, this.requestController.signal)
      .then(cards => {
        if (token !== this.requestToken) {
          return;
        }

        if (cards.length < 3) {
          this.destroySlider();
          this.rail.replaceChildren();
          this.setStatus('', false);
          this.setVisible(false);
          return;
        }

        this.setVisible(true);
        this.renderProducts(cards);
      })
      .catch(error => {
        if (error.name === 'AbortError' || token !== this.requestToken) {
          return;
        }

        this.destroySlider();
        this.rail.replaceChildren();
        this.setStatus('', false);
        this.setVisible(false);
      });
  }

  renderLoading() {
    this.destroySlider();
    this.rail.replaceChildren();

    const loading = document.createElement('div');
    loading.className = 'f-cart-cross-sells__loading';

    const loadingLabel = this.dataset.loadingLabel || '';
    loading.setAttribute('role', 'status');
    loading.setAttribute('aria-live', 'polite');
    loading.setAttribute('aria-label', loadingLabel);

    const spinner = document.createElement('span');
    spinner.className = 'f-cart-cross-sells__spinner';
    spinner.setAttribute('aria-hidden', 'true');

    loading.append(spinner);
    this.rail.append(loading);
    this.setStatus('', true);
  }

  async loadRecommendations(cartItems, signal) {
    const productIds = [...new Set(
      cartItems
        .map(item => String(item.product_id || ''))
        .filter(Boolean)
    )];
    const cartProductIds = new Set(productIds);

    const complementaryResults = await Promise.all(
      productIds.map(productId => this.fetchRecommendations(productId, 'complementary', signal))
    );
    let cards = this.uniqueCards(complementaryResults.flat(), cartProductIds);

    if (cards.length < 3) {
      const relatedResults = await Promise.all(
        productIds.map(productId => this.fetchRecommendations(productId, 'related', signal))
      );
      cards = this.uniqueCards([...cards, ...relatedResults.flat()], cartProductIds);
    }

    return cards.slice(0, 3);
  }

  async fetchRecommendations(productId, intent, signal) {
    const url = new URL(this.dataset.recommendationsUrl, window.location.origin);
    url.searchParams.set(
      'section_id',
      this.dataset.recommendationsSectionId || 'cart-drawer-cross-sells'
    );
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', '10');
    url.searchParams.set('intent', intent);

    try {
      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        signal,
        headers: {
          Accept: 'text/html'
        }
      });

      if (!response.ok) {
        throw new Error(`Unable to load recommendations: ${response.status}`);
      }

      const markup = await response.text();
      const wrapper = document.createElement('div');
      wrapper.innerHTML = markup;
      return [...wrapper.querySelectorAll('.f-cart-cross-sells__card')];
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error;
      }

      return [];
    }
  }

  uniqueCards(cards, cartProductIds) {
    const seen = new Set();

    return cards.filter(card => {
      const productId = String(card.dataset.productId || '');
      if (!productId || seen.has(productId) || cartProductIds.has(productId)) {
        return false;
      }

      seen.add(productId);
      this.prepareQuickBuy(card);
      return true;
    });
  }

  prepareQuickBuy(card) {
    card.querySelectorAll('femtis-quick-buy').forEach(quickBuy => {
      quickBuy.classList.add('f-cart-cross-sells__quick-buy', 'swiper-no-swiping');

      // Keep the theme's existing quick-buy component and markup. This class
      // only lets the cart drawer scope its already-rendered popup on desktop.
      const popup = quickBuy
        .querySelector('.f-overlay-tmpl')
        ?.content
        .querySelector('.f-product-quick-add');
      popup?.classList.add(
        'f-cart-cross-sells__quick-buy-popup',
        'swiper-no-swiping'
      );
    });
  }

  renderProducts(cards) {
    this.destroySlider();
    this.rail.replaceChildren(...cards);
    this.setStatus('', false);
    this.setExpanded(cartCrossSellsPageState.expanded);
  }

  getSwiperConstructor() {
    const existing = document.querySelector('.swiper-container.swiper-container-initialized');
    return this.slider?.swiper?.constructor || existing?.swiper?.constructor || window.Swiper;
  }

  scheduleSliderInit() {
    if (this.sliderInitTimer || this.swiper || this.panel?.hidden) {
      return;
    }

    let tries = 0;
    const attempt = () => {
      this.sliderInitTimer = null;

      if (!this.isConnected || this.initSlider()) {
        return;
      }

      if (++tries < 40) {
        this.sliderInitTimer = window.setTimeout(attempt, 100);
      }
    };

    attempt();
  }

  initSlider() {
    if (this.panel?.hidden) {
      return false;
    }

    if (!this.slider || this.swiper || this.slider.classList.contains('swiper-container-initialized')) {
      return true;
    }

    const Swiper = this.getSwiperConstructor();
    if (typeof Swiper !== 'function') {
      return false;
    }

    this.swiper = new Swiper(this.slider, {
      slidesPerView: 2.6,
      spaceBetween: 15,
      slidesOffsetBefore: 24,
      slidesOffsetAfter: 24,
      freeMode: true,
      grabCursor: true,
      breakpoints: {
        768: {
          slidesPerView: 3,
          spaceBetween: 15
        }
      }
    });

    return true;
  }

  destroySlider() {
    if (this.sliderInitTimer) {
      window.clearTimeout(this.sliderInitTimer);
      this.sliderInitTimer = null;
    }

    if (this.swiper?.destroy) {
      this.swiper.destroy(true, true);
    }

    this.swiper = null;
  }
}

if (!customElements.get('cart-cross-sells')) {
  customElements.define('cart-cross-sells', CartCrossSells);
}
